import { FC, useCallback, useState } from 'react';
import { Button, Input, Typography, message } from 'antd';
import { useAuthDispatchContext, useAuthStateContext } from 'providers/Auth';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import VerificationInput from 'react-verification-input';

import useTwoFaLockout from 'hooks/useTwoFaLockout';
import {
  isRecoveryCode,
  normalizeRecoveryCode,
  sanitizeRecoveryCodeInput
} from 'utils/recoveryCode';
import { Content, InputContainer, LinkWrapper } from './styled';
import { refreshTokenStorage, tokenStorage } from 'utils/storage';

const { Title, Link: TextLink } = Typography;

const Auth2FA: FC = () => {
  const [value, setValue] = useState('');
  const [isLoading, setLoading] = useState(false);
  // The lost-phone path. Same field server-side, but a recovery code is
  // not six digits, so the digit-only input cannot carry one.
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);

  const navigate = useNavigate();
  const { tmpToken } = useAuthStateContext();
  const { verify2FA, getUserData } = useAuthDispatchContext();
  const { isLocked, readError } = useTwoFaLockout();

  const onFinish = useCallback(
    async (code?: string) => {
      setLoading(true);

      try {
        // The server strips punctuation itself, but sending the clean
        // form keeps what we send and what we validate identical.
        const otp = useRecoveryCode
          ? normalizeRecoveryCode(code || value)
          : code || value;

        const data = await verify2FA(otp, tmpToken);

        tokenStorage.set(data.accessToken);
        refreshTokenStorage.set(data.refreshToken);
        getUserData();
      } catch (err) {
        setLoading(false);

        const { status, message: text } = readError(err);

        // The tmpToken is single-use, but a WRONG code does not spend
        // it — only a successful verify does. So retry in place on a
        // 400, and go back to sign-in only when the session is really
        // gone (401: expired or already spent).
        if (status === 401) {
          message.error(text);
          navigate('/login');

          return;
        }

        message.error(text);
      }
    },
    [
      verify2FA,
      value,
      useRecoveryCode,
      tmpToken,
      getUserData,
      readError,
      navigate
    ]
  );

  return (
    <Content>
      <Title level={2} style={{ marginBottom: 24 }}>
        Two-Factor Authentication
      </Title>
      <InputContainer>
        {useRecoveryCode ? (
          <Input
            autoFocus
            size="large"
            value={value}
            maxLength={11}
            placeholder="XXXXX-XXXXX"
            onChange={e => setValue(sanitizeRecoveryCodeInput(e.target.value))}
            data-test-id="2fa-recovery-code-input"
          />
        ) : (
          <VerificationInput
            classNames={{
              container: 'container',
              character: 'character',
              characterInactive: 'character--inactive',
              characterSelected: 'character--selected'
            }}
            placeholder=""
            autoFocus
            onComplete={onFinish}
            validChars="0-9"
            onChange={code => setValue(code)}
            value={value}
          />
        )}
      </InputContainer>
      <Button
        type="primary"
        onClick={() => onFinish()}
        shape="round"
        loading={isLoading}
        disabled={
          isLocked ||
          (useRecoveryCode ? !isRecoveryCode(value) : value.length !== 6)
        }
        block
      >
        Submit
      </Button>
      <LinkWrapper>
        <TextLink
          onClick={() => {
            setUseRecoveryCode(prev => !prev);
            setValue('');
          }}
          style={{ fontSize: 12 }}
          data-test-id="2fa-toggle-recovery-code"
        >
          {useRecoveryCode ? 'Use a code from the app' : 'Use a recovery code'}
        </TextLink>
      </LinkWrapper>
      <LinkWrapper>
        <Link to="/login" style={{ fontSize: 12, color: '#2D2C4C' }}>
          <ArrowLeftOutlined style={{ marginRight: 12 }} />
          Back to Login
        </Link>
      </LinkWrapper>
    </Content>
  );
};

export default Auth2FA;
